import { Icon, spacing } from '@scality/core-ui';
import { Button } from '@scality/core-ui/dist/next';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { FollowUpQuestion } from '../ai-tools/FollowUpQuestions/FollowUpQuestions';

const MORE_OPTIONS_TOOLTIP = { overlay: 'More options' };

type SupportPromptGroupProps = {
  onClick: (followUpQuestion: React.MouseEvent<HTMLButtonElement>) => void;
  followUpQuestions: FollowUpQuestion[];
};

const FloatingPromptGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
  gap: ${spacing.r8};
  position: absolute;
  // Places the floating prompt group above and to the top right of the more options button
  // It moves from 100% of its size up left, minus 21px the width of the more options button
  // and 8px for the margin
  transform: translate(calc(-100% + 21px), calc(-100% - 8px));
`;

export const SupportPromptGroupMore: React.FC<SupportPromptGroupProps> = ({ onClick, followUpQuestions }) => {
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const openShowMoreOptions = useCallback(() => setShowMoreOptions(true), []);
  const closeShowMoreOptions = useCallback(() => setShowMoreOptions(false), []);

  const moreButtonWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!moreButtonWrapperRef.current) return;

    const outsideClickHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!moreButtonWrapperRef.current?.contains(target)) {
        closeShowMoreOptions();
      }
    };

    const pressEscapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeShowMoreOptions();
      }
    };

    document.addEventListener('keydown', pressEscapeHandler);
    document.addEventListener('click', outsideClickHandler);

    return () => {
      document.removeEventListener('keydown', pressEscapeHandler);
      document.removeEventListener('click', outsideClickHandler);
    };
  }, [closeShowMoreOptions]);

  return (
    <>
      {showMoreOptions && (
        <FloatingPromptGroup>
          {followUpQuestions.map((followupquestion, idx) => (
            <Button
              key={followupquestion.id}
              label={followupquestion.title}
              variant="primary"
              value={followupquestion.id}
              onClick={onClick}
              tabIndex={idx + 2} // Ensure tab order is after the toggle button (which defaults to tabIndex=1)
            />
          ))}
        </FloatingPromptGroup>
      )}
      <div ref={moreButtonWrapperRef}>
        <Button
          icon={<Icon name="More" />}
          id="more-options-button"
          variant={showMoreOptions ? 'primary' : 'secondary'}
          tooltip={MORE_OPTIONS_TOOLTIP}
          onClick={showMoreOptions ? closeShowMoreOptions : openShowMoreOptions}
          aria-expanded={showMoreOptions}
        />
      </div>
    </>
  );
};
